import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import useScopedTranslation from 'hooks/useScopedTranslation';
import {
  getValidators,
  upsertValidator,
  setValidatorActive,
} from 'services/escriturasValidatorsService';
import { getWritingTypes } from 'services/escriturasTemplatesService';
import { useSnackbar } from 'context/SnackbarContext';
import ValidatorEditModal from './ValidatorEditModal';
import { eppValidatorsI18n } from './i18n';
import '../../../../../styles/naEeppTableGrid.scss';

/**
 * Configuración de validadores EEPP (lista + editar). Mínimo: sin paginador,
 * búsqueda ni crear (los validadores son usuarios EEPP existentes, ~4 filas).
 * Espejo liviano de ConfigNotaries. Funciona como ruta top-level (DS por prop).
 */
export default function EEPPValidators({
  DesignSystemComponents: dsProp,
} = {}) {
  const { t } = useScopedTranslation(eppValidatorsI18n);
  const { showSnackbar } = useSnackbar();

  const outletContext = useOutletContext() ?? {};
  const DesignSystemComponents = dsProp ?? outletContext.DesignSystemComponents;
  const { Button, Table, Badge, IconButton, Modal, Card, Input, ComboBox } =
    DesignSystemComponents ?? {};

  const [validators, setValidators] = useState([]);
  const [writingTypes, setWritingTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const abortRef = useRef(null);

  // Mapa code → displayName del catálogo (para mostrar los tipos en la tabla).
  const displayNameByCode = useMemo(() => {
    const map = new Map();
    (writingTypes || []).forEach((typ) => {
      if (typ.code)
        map.set(String(typ.code).toUpperCase(), typ.displayName || typ.code);
    });
    return map;
  }, [writingTypes]);

  const load = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      setLoading(true);
      const [validatorList, types] = await Promise.all([
        getValidators(controller.signal),
        getWritingTypes(controller.signal),
      ]);
      setValidators(Array.isArray(validatorList) ? validatorList : []);
      setWritingTypes(Array.isArray(types) ? types : []);
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;
      showSnackbar(
        err.customMessage || t('eepp_validators_error_load'),
        'error'
      );
    } finally {
      setLoading(false);
    }
  }, [showSnackbar, t]);

  useEffect(() => {
    load();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [load]);

  const openEdit = (row) => {
    setEditing(row);
    setIsModalOpen(true);
  };

  const handleSave = async ({
    userId,
    writingTypeCodes,
    capacityTiempos,
    supervisorUserId,
    isActive,
  }) => {
    try {
      await upsertValidator(userId, {
        writingTypeCodes,
        capacityTiempos,
        supervisorUserId,
      });

      // El toggle de activación es un endpoint aparte; solo lo llamamos si cambió
      // respecto al estado configurado actual (el upsert no toca IsActive).
      const previous = validators.find((v) => v.userId === userId);
      const wasActive = previous?.configured ? previous.isActive : true;
      if (isActive !== wasActive) {
        await setValidatorActive(userId, isActive);
      }

      showSnackbar(t('eepp_validators_success_saved'), 'success');
      setIsModalOpen(false);
      setEditing(null);
      await load();
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.Message ||
        err.customMessage ||
        t('eepp_validators_error_save');
      showSnackbar(message, 'error');
    }
  };

  const renderTypes = (row) => {
    if (!row.writingTypeCodes || row.writingTypeCodes.length === 0) {
      return (
        <span style={{ color: '#94A3B8' }}>
          {t('eepp_validators_no_types')}
        </span>
      );
    }
    const labels = row.writingTypeCodes.map(
      (code) => displayNameByCode.get(String(code).toUpperCase()) || code
    );
    return (
      <div className='d-flex flex-wrap' style={{ gap: 4 }}>
        {labels.map((label, idx) =>
          Badge ? (
            <Badge
              key={`${label}-${idx}`}
              text={label}
              color='light'
              size='sm'
            />
          ) : (
            <span key={`${label}-${idx}`} className='badge bg-light text-dark'>
              {label}
            </span>
          )
        )}
      </div>
    );
  };

  const statusBadge = (row) => {
    if (!row.configured) {
      return {
        color: 'light',
        label: t('eepp_validators_status_unconfigured'),
      };
    }
    return row.isActive
      ? { color: 'success', label: t('eepp_validators_status_active') }
      : { color: 'default', label: t('eepp_validators_status_inactive') };
  };

  if (!Table || !Badge || !IconButton) return null;

  const Wrapper = Card || 'div';
  const wrapperProps = Card
    ? { specialPadding: 'p-4' }
    : { className: 'container-fluid p-4' };

  return (
    <Wrapper {...wrapperProps}>
      <div className='mb-3'>
        <h2 className='mb-1'>{t('eepp_validators_title')}</h2>
        <p className='caption font-light mb-0'>
          {t('eepp_validators_subtitle')}
        </p>
      </div>

      <div className='table-container na-eepp-grid-6'>
        <Table
          columns={[
            { accessor: 'userName', label: t('eepp_validators_col_name') },
            {
              accessor: 'types',
              label: t('eepp_validators_col_types'),
              render: renderTypes,
            },
            {
              accessor: 'capacityTiempos',
              label: t('eepp_validators_col_capacity'),
              render: (row) => (row.configured ? row.capacityTiempos : '—'),
            },
            {
              accessor: 'supervisorUserName',
              label: t('eepp_validators_col_supervisor'),
              render: (row) =>
                row.supervisorUserName || (
                  <span style={{ color: '#94A3B8' }}>
                    {t('ev_supervisor_none')}
                  </span>
                ),
            },
            {
              accessor: 'status',
              label: t('eepp_validators_col_status'),
              render: (row) => {
                const badge = statusBadge(row);
                return (
                  <Badge text={badge.label} color={badge.color} size='sm' />
                );
              },
            },
            {
              label: t('eepp_validators_col_actions'),
              render: (row) => (
                <IconButton
                  ariaLabel={t('eepp_validators_action_edit')}
                  icon='pencil'
                  onClick={() => openEdit(row)}
                  size='md'
                  iconStroke='var(--primary-500)'
                />
              ),
            },
          ]}
          data={validators}
          loading={loading}
        />
        {!loading && validators.length === 0 && (
          <div className='text-center p-4'>
            <p className='caption font-light'>
              {t('eepp_validators_no_results')}
            </p>
          </div>
        )}
      </div>

      <ValidatorEditModal
        DesignSystemComponents={{ Modal, Input, Button, ComboBox }}
        show={isModalOpen}
        validator={editing}
        writingTypes={writingTypes}
        onClose={() => {
          setIsModalOpen(false);
          setEditing(null);
        }}
        onSave={handleSave}
      />
    </Wrapper>
  );
}
